import { FormEvent, Suspense, useCallback } from 'react';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, Stack } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import ApiErrorAlert from '@/components/feedback/Error/ApiErrorAlert';
import usePageTitle from '@/hooks/usePageTitle';
import { CreateFormProps } from './types';

function CreateForm<TData, TError, TVariables, TContext>({
  title,
  getFormValues,
  resetFormValues,
  backUri,
  children,
  mutation,
}: CreateFormProps<TData, TError, TVariables, TContext>) {
  usePageTitle(title);

  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event && event.preventDefault();

      const values = getFormValues();
      if (values !== undefined) {
        await mutation.mutateAsync(values).finally(resetFormValues);

        navigate(backUri);
      }
    },
    [getFormValues, mutation, resetFormValues, navigate, backUri]
  );

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Grid container item pt={2} spacing={2} component="form" onSubmit={handleSubmit} data-testid="create-form">
        <Grid item xs={12}>
          <h1>{title}</h1>
        </Grid>
        {mutation.isError ? (
          <Grid item xs={12}>
            <ApiErrorAlert error={mutation.error} />
          </Grid>
        ) : null}

        {children}

        <Grid item xs={12}>
          <Stack spacing={2} direction="row">
            <Button variant="contained" component={Link} to={backUri} startIcon={<NavigateBeforeIcon />}>
              Back
            </Button>
            <LoadingButton loading={mutation.isLoading} variant="contained" type="submit" loadingPosition="end" endIcon={<SaveIcon />}>
              Save
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </Suspense>
  );
}

export default CreateForm;
